import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import PageHeaderText from 'components/base/page-header-text';
import FeedCard from 'components/feed-card';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiService } from 'services/base.service';
import { MealType } from 'utils/types';
import { ReactComponent as EditIcon } from 'assets/icons/edit-meal-icon.svg';
import { ReactComponent as ArchiveIcon } from 'assets/icons/archive-icon.svg';
import DeleteModal from 'components/base/delete-modal';
import InfiniteScroll from 'react-infinite-scroll-component';
import './styles.scss';
import useDashboardContext from 'contexts/dashboard.context';
import { ROUTE_PREFIX } from 'utils/constants';
import AccessDenied from 'components/access-denied';

const BaseMeal = () => {
  const navigate = useNavigate();
  const { setIsCustomSearch, customSearch, permissions } = useDashboardContext();
  const [baseMeals, setBaseMeals] = useState([] as MealType[]);
  const [active, setActive] = useState({} as MealType);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const getBaseMeals = async () => {
    try {
      if (page === 1) setLoading(true);
      const params: { page: number; search?: string; active?: boolean } = { page };
      if (customSearch !== '') {
        params.search = customSearch;
      }
      const res: any = await apiService.get('base/meals', {
        params
      });
      if (res?.data) {
        setBaseMeals(page === 1 ? res.data.data.data : baseMeals.concat(res.data.data.data));
        setTotal(res.data.data.pagination.total);
      }
    } catch {
      toast(<ToastMessage type='error' message={'Error fetching base meals'} />);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getBaseMeals();
  }, [page]);
  useEffect(() => {
    const getMeals = setTimeout(() => {
      setPage(1);
    }, 1000);

    return () => {
      clearTimeout(getMeals);
    };
  }, [customSearch]);
  const toggleDeleteModal = () => {
    setShowDeleteModal(!showDeleteModal);
  };

  const handleDeleteClick = async () => {
    try {
      const res: any = await apiService.delete(`base/meal/${active.id}`, {});
      if (res?.data) {
        setBaseMeals(baseMeals.filter((baseMeal) => baseMeal.id !== active.id));
        toast(<ToastMessage type='success' message={'Base meal deleted'} />);
        toggleDeleteModal();
      }
    } catch {
      toast(<ToastMessage type='error' message={'Error deleting base meals'} />);
    }
  };

  const fetchMoreData = () => {
    setPage((p) => p + 1);
  };

  useEffect(() => {
    setIsCustomSearch(true);
    return () => {
      setIsCustomSearch(false);
    };
  }, []);

  if (!permissions.canAccessBaseItems) return <AccessDenied />;

  return (
    <div>
      <PageHeaderText title='Base meals'>
        <div className='flex items-center gap-5 '>
          <Button
            color='primary'
            variant='link'
            className='cursor-pointer text-sm font-medium text-[#E60A2B] w-fit'
            onClick={() => navigate(`${ROUTE_PREFIX.FOOD}/create-base-meal`)}>
            + Add new
          </Button>
          <Button
            small
            className=''
            variant='block'
            color='primary'
            onClick={() => navigate(`${ROUTE_PREFIX.FOOD}/upload-base-meal`)}>
            Add via xls
          </Button>
        </div>
      </PageHeaderText>
      <div>
        {loading ? (
          <div className='w-full mt-10 flex justify-center items-center'>
            <p className='font-medium text-lg'>Loading...</p>
          </div>
        ) : baseMeals?.length === 0 ? (
          <div className='w-full mt-10 flex justify-center items-center'>
            <p className='font-medium text-lg'>No base meal available</p>
          </div>
        ) : (
          <InfiniteScroll
            className='no-scrollbar'
            dataLength={baseMeals.length}
            next={() => fetchMoreData()}
            hasMore={total > baseMeals.length}
            loader={<h4 className='text-center'>Loading...</h4>}
            height='75vh'
            endMessage={
              <p className='text-center'>
                <b>Yay! You have seen it all</b>
              </p>
            }>
            <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5'>
              {baseMeals?.map((baseMeal) => (
                <FeedCard
                  key={baseMeal.id}
                  image={baseMeal.images?.[0]}
                  className='mb-2'
                  actions={
                    <div className='flex gap-2 absolute top-2 right-2'>
                      <ArchiveIcon
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDeleteModal(true);
                          setActive(baseMeal);
                        }}
                        className={` w-7 h-7 text-white bg-black hover:bg-black bg-opacity-70 rounded-md p-[7.5px] transition-none z-10`}
                      />
                      <EditIcon
                        onClick={() =>
                          navigate(`${ROUTE_PREFIX.FOOD}/edit-base-meal/${baseMeal.id}`)
                        }
                        className=' w-7 h-7 text-white bg-black hover:bg-black bg-opacity-70 rounded-md p-[7.5px] transition-none z-10 edit_icon'
                      />
                    </div>
                  }>
                  <div className='flex justify-between items-center mt-2'>
                    <div>
                      <p className='text-sm'>{baseMeal?.name}</p>
                    </div>
                  </div>
                </FeedCard>
              ))}
            </div>
          </InfiniteScroll>
        )}
      </div>
      <DeleteModal
        show={showDeleteModal}
        onClose={toggleDeleteModal}
        handleDelete={handleDeleteClick}
        title={`Are you sure you want to delete ${active?.name} ?`}
        description='This action cannot be undone'
        loading={loading}
      />
    </div>
  );
};

export default BaseMeal;
